import React, { useState, useEffect } from "react";
import {
  Modal,
  Steps,
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  notification,
} from "antd";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { createWorker } from "../services/workersAPI";
import { useAuth } from "../hooks/useAuth";
import { fetchProjectUsers } from "../services/projectsAPI";
import dayjs from "dayjs";

const { TextArea } = Input;

const DATA_SOURCE_OPTIONS = [
  { value: "rfi", label: "RFIs" },
  { value: "submittal", label: "Submittals" },
];

const FREQUENCY_OPTIONS = [
  { value: "once", label: "Once" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];

const StepFormItems = {
  BasicInfo: ({ form }) => (
    <>
      <Form.Item
        name="name"
        label="Name"
        rules={[{ required: true, message: "Please input the worker name!" }]}
        extra="Use a concise title (few words) like 'Past Due Open RFIs' or 'Submittals in Alexa's court'."
      >
        <Input />
      </Form.Item>
      <Form.Item
        name="data_source"
        label="Data Source"
        rules={[{ required: true, message: "Please select the data source!" }]}
      >
        <Select>
          {DATA_SOURCE_OPTIONS.map((option) => (
            <Select.Option key={option.value} value={option.value}>
              {option.label}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
    </>
  ),

  Prompt: () => (
    <Form.Item
      name="prompt"
      label="Worker Prompt"
      rules={[{ required: true, message: "Please input the prompt!" }]}
      extra="Provide a clear instruction, e.g., 'Report open RFIs that are past due and show how many days past due' or 'List open submittals that are in John Doe's court'."
    >
      <TextArea rows={4} />
    </Form.Item>
  ),

  Schedule: () => (
    <>
      <Form.Item
        name="start_date"
        label="Start Date"
        rules={[
          {
            required: true,
            message: "Please select the start date!",
            type: "object",
          },
          {
            validator: (_, value) => {
              if (value && value.isBefore(dayjs(), "minute")) {
                return Promise.reject("Start date cannot be in the past!");
              }
              return Promise.resolve();
            },
          },
        ]}
      >
        <DatePicker
          showTime={{ format: "HH:mm:ss" }}
          disabledDate={(current) => {
            return current && current < dayjs().startOf("day");
          }}
          disabledTime={(current) => {
            if (current && current.isSame(dayjs(), "day")) {
              return {
                disabledHours: () => Array.from(Array(dayjs().hour()).keys()),
                disabledMinutes: (selectedHour) => {
                  if (selectedHour === dayjs().hour()) {
                    return Array.from(Array(dayjs().minute()).keys());
                  }
                  return [];
                },
              };
            }
            return {};
          }}
        />
      </Form.Item>
      <Form.Item
        name="frequency"
        label="Frequency"
        rules={[{ required: true, message: "Please select the frequency!" }]}
      >
        <Select>
          {FREQUENCY_OPTIONS.map((option) => (
            <Select.Option key={option.value} value={option.value}>
              {option.label}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
    </>
  ),

  Recipients: ({ users, selectedUsers, setSelectedUsers }) => (
    <Form.Item
      name="recipients"
      label="Recipients"
      rules={[
        { required: true, message: "Please select at least one recipient!" },
      ]}
    >
      <Select
        mode="multiple"
        style={{ width: "100%" }}
        placeholder="Select recipients"
        value={selectedUsers}
        onChange={setSelectedUsers}
        optionFilterProp="label"
        options={users?.map((user) => ({
          value: user.id || user._id,
          label: `${user.username} (${user.email})`,
        }))}
      />
    </Form.Item>
  ),
};

const STEPS = [
  { title: "Basic Info", Component: StepFormItems.BasicInfo },
  { title: "Prompt", Component: StepFormItems.Prompt },
  { title: "Schedule", Component: StepFormItems.Schedule },
  { title: "Recipients", Component: StepFormItems.Recipients },
];

const CreateWorkerModal = ({ visible, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [form] = Form.useForm();

  const { selectedProject } = useAuth();
  const projectId = selectedProject.project_id;
  const queryClient = useQueryClient();

  const { data: users } = useQuery({
    queryKey: ["projectUsers", projectId, 1, 1000],
    queryFn: () => fetchProjectUsers(projectId, 1, 1000),
    enabled: !!projectId,
  });

  const resetForm = () => {
    form.resetFields();
    setFormData({});
    setCurrentStep(0);
    setSelectedUsers([]);
  };

  useEffect(() => {
    if (!visible) {
      resetForm();
    }
  }, [visible]);

  const createMutation = useMutation({
    mutationFn: createWorker,
    onSuccess: () => {
      queryClient.invalidateQueries(["workers", projectId]);
      notification.success({ message: "Worker created successfully" });
      resetForm();
      onClose();
    },
    onError: (error) => {
      notification.error({
        message: "Failed to create worker",
        description: error.message,
      });
    },
  });

  const handleNext = async () => {
    try {
      const values = await form.validateFields();
      setFormData({ ...formData, ...values });
      form.setFieldsValue(values);
      setCurrentStep(currentStep + 1);
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  const handleFinish = async () => {
    try {
      const values = await form.validateFields();
      const allFormData = { ...formData, ...values };

      if (allFormData.start_date.isBefore(dayjs())) {
        notification.error({
          message: "Invalid start date",
          description: "Start date cannot be in the past!",
        });
        return;
      }

      const finalFormData = {
        name: allFormData.name,
        data_source: allFormData.data_source,
        prompt: allFormData.prompt,
        frequency: allFormData.frequency,
        project_id: projectId,
        report_to_emails: selectedUsers.map(
          (userId) => users?.data.find((u) => u.id === userId)?.email
        ),
        start_date: allFormData.start_date.format(),
      };

      createMutation.mutate(finalFormData);
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const CurrentStepComponent = STEPS[currentStep].Component;

  return (
    <Modal
      title="Create Worker"
      open={visible}
      onCancel={handleClose}
      width={800}
      destroyOnClose={true}
      footer={null}
    >
      <Steps
        current={currentStep}
        items={STEPS.map((step) => ({ title: step.title }))}
        className="mb-8"
      />
      <Form form={form} layout="vertical" initialValues={formData}>
        <CurrentStepComponent
          form={form}
          users={users?.data}
          selectedUsers={selectedUsers}
          setSelectedUsers={setSelectedUsers}
        />
        <div className="flex justify-end gap-2 mt-4">
          {currentStep > 0 && (
            <Button onClick={() => setCurrentStep(currentStep - 1)}>
              Previous
            </Button>
          )}
          {currentStep < STEPS.length - 1 && (
            <Button type="primary" onClick={handleNext}>
              Next
            </Button>
          )}
          {currentStep === STEPS.length - 1 && (
            <Button
              type="primary"
              onClick={handleFinish}
              loading={createMutation.isPending}
            >
              Create
            </Button>
          )}
        </div>
      </Form>
    </Modal>
  );
};

export default CreateWorkerModal;
